/**
 * resultCopy.ts — the words the eligible Result screen ADDS to talk to the
 * patient (premium pass, 2026-09-24, Build B).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey look-and-feel,
 * loader + Result.
 *
 * CTO: "You can do way better on this screen … don't remove any of these things
 * … talk to the patient … they are on their journey to complete it, lose
 * weight, and it's just a few steps more." So the screen keeps every element it
 * had and adds a greeting, a short encouraging line and a "Your journey"
 * tracker. They live here, in one module, so the rules below are swept in one
 * place (`ResultScreen.spec.tsx`).
 *
 * ⚠️ WHAT THESE LINES MAY NEVER SAY (CAP Code 13 — weight control — and 3.7;
 * the DMCC Act 2024):
 *  - NO OUTCOME PROMISE. "You've done the hardest part" is about the health
 *    check the patient has just finished, never about losing weight. No kg, no
 *    "results", no "guaranteed", no time claim ("in 5 minutes", "by Friday").
 *    The projection keeps its own mandatory disclaimer beside the figure.
 *  - NO NEW TRUST OR REGULATORY CLAIM. The trust row reuses the kit's
 *    `TrustStrip` (its "UK clinicians" and "GPhC-registered pharmacy" chips, the
 *    claims the payment step already makes; the second said "GMC/GPhC pharmacy"
 *    until review 2026-09-25, which a pharmacy cannot be) and this screen's own
 *    "Discreet delivery" step. Never a CQC/GPhC/MHRA statement of our own, a registration number, a
 *    rating, a statistic or a testimonial.
 *  - NOTHING ABOUT THE VERDICT BEYOND WHAT THE HEADING ALREADY SAYS. The
 *    clinician review is still to come (and is on the tracker).
 */
import { bmiBandFor } from './BmiDial';

/** "Discreet delivery" — this screen's own what-happens-next step, reused as the third trust chip. */
export const DISCREET_DELIVERY = 'Discreet delivery';

/** The encouraging line — about the check just finished, never about weight. */
export const RESULT_ENCOURAGEMENT = 'You’ve done the hardest part.';

/** The tracker's line — how much is left, in words, never a count or a time. */
export const RESULT_STEPS_LEFT = 'Just a few steps left';

/** The tracker's heading. */
export const RESULT_JOURNEY_HEADING = 'Your journey';

export type JourneyTrackerState = 'done' | 'next' | 'upcoming';

/**
 * The step the tracker marks NEXT — the plan screen "See my plan" opens. Named
 * (2026-09-25) because it is a SOURCE: the checkout loader card's first step,
 * `CHOOSE_TREATMENT_AND_PLAN` (`checkout/planStepCopy.ts`), restates it joined
 * to the plan screen's own first question, and `checkoutLoaderContent.spec`
 * pins that it does — so a reword here turns that spec red rather than leaving
 * the loader describing a step this tracker no longer names.
 */
export const RESULT_PLAN_STEP = 'Choose your plan';

/**
 * The patient's path from here, in the order they meet it: the plan screen, the
 * delivery & card step (a HOLD — nothing is charged there), then a clinician's
 * review. Guests also create an account after the card; that is a
 * confirmation of the order, not a step toward it, so it is not listed.
 */
export const RESULT_JOURNEY_STEPS: ReadonlyArray<{ label: string; state: JourneyTrackerState }> = [
  { label: 'Health check', state: 'done' },
  { label: RESULT_PLAN_STEP, state: 'next' },
  { label: 'Delivery & card', state: 'upcoming' },
  { label: 'Clinician review', state: 'upcoming' },
];

/** Screen-reader words after each tracker step — the state is never colour alone. */
export const JOURNEY_TRACKER_STATE_WORDS: Record<JourneyTrackerState, string> = {
  done: ', done',
  next: ', next',
  upcoming: ', still to come',
};

/**
 * A first name fit to greet someone with, or null.
 *
 * PII RULE: the name is only ever the one the patient typed into Step 0's
 * "First name" field in this journey, held in memory by JourneyPage. Nothing is
 * fetched for it, nothing is logged, and it never leaves the page. Only its
 * first word greets (someone who typed "Sam Jones" into the first-name box is
 * greeted "Sam"), and anything that does not look like a given name (an email
 * pasted into the field, digits, a 40-character string) gets no name rather
 * than a strange one.
 */
export function greetingName(raw: string | null | undefined): string | null {
  const first = (raw ?? '').trim().split(/\s+/)[0] ?? '';
  return /^\p{L}[\p{L}'’-]{0,23}$/u.test(first) ? first : null;
}

/** The warm eyebrow above the verdict: "Well done, Sam", or just "Well done". */
export function resultGreeting(firstName: string | null): string {
  return firstName ? `Well done, ${firstName}` : 'Well done';
}

/**
 * The line under the BMI dial (2026-09-26, Ashley Haveloch-Jones via the CTO:
 * the dial "just tells me it factually" — it did not say which range was high).
 * It names the patient's NHS band in plain words, then says what the band means
 * HERE: it is where a clinician can consider this treatment — the claim the old
 * line ("Your BMI sits in the range where this treatment is clinically
 * considered.") already made, in the patient's words.
 *
 * ⚠️ Factual, never alarm: the band's own name and nothing about danger or risk.
 * A BMI the dial has no band for (off the scale's low end) keeps the old
 * sentence, which makes no claim about a band.
 */
export function bmiCaption(bmi: number): string {
  const band = bmiBandFor(bmi);
  if (!band || band.key === 'healthy') return BMI_CAPTION_FALLBACK;
  return `Your BMI is in the ${band.label.toLowerCase()} range. That’s where a clinician can consider this treatment.`;
}

/** The pre-2026-09-26 caption, kept for a BMI with no band to name. */
export const BMI_CAPTION_FALLBACK = 'Your BMI sits in the range where this treatment is clinically considered.';
